import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import LeavesListView from '../../../components/hr/leaves/LeavesListView';
import LeavesCalendarView from '../../../components/hr/leaves/LeavesCalendarView';
import LeavesPersonalView from '../../../components/hr/leaves/LeavesPersonalView';
import { Button, Segmented, Space, Card } from 'antd';
import { PlusOutlined, UnorderedListOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { leavesApi } from '../../../utils/api';
import PermissionGate from '../../../components/PermissionGate';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import moment from 'moment-jalaali';
import { getLast30DaysRange } from '../../../utils/dateUtils';
import { useListReturnState } from '../../../hooks/useListReturnState';

const EMPTY_FILTER_OPTIONS = {
  employees: [],
  leave_types: [],
  statuses: [],
};

/** استخراج filters از پاسخ ایندکس/تقویم مرخصی */
const extractLeaveFilters = (res) => {
  const root =
    res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
  const filters = root?.filters;
  if (!filters || typeof filters !== 'object') return null;
  return {
    employees: Array.isArray(filters.employees) ? filters.employees : [],
    leave_types: Array.isArray(filters.leave_types) ? filters.leave_types : [],
    statuses: Array.isArray(filters.statuses) ? filters.statuses : [],
  };
};

const Leaves = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = useResourcePermissions('leaves');
  const [viewMode, setViewMode] = useState('list');
  const [listData, setListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listPagination, setListPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const defaultListRange = useMemo(() => getLast30DaysRange(), []);
  const [listFilters, setListFilters] = useState({
    startDate: defaultListRange.startDate,
    endDate: defaultListRange.endDate,
  });
  const [calendarLeaves, setCalendarLeaves] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const calendarRequestId = useRef(0);
  const [personalData, setPersonalData] = useState(null);
  const [personalLoading, setPersonalLoading] = useState(false);

  const captureCurrentListState = useCallback(
    () => ({
      viewMode,
      listFilters,
    }),
    [viewMode, listFilters],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.viewMode) setViewMode(restored.viewMode);
    if (restored.listFilters) setListFilters(restored.listFilters);
  });

  const applyFilterOptions = useCallback((res) => {
    const next = extractLeaveFilters(res);
    if (next) setFilterOptions(next);
  }, []);

  const loadListData = async (filters) => {
    const nextFilters = filters != null ? { ...listFilters, ...filters } : listFilters;
    if (filters != null) setListFilters(nextFilters);
    setListLoading(true);
    try {
      const res = await leavesApi.getAll(nextFilters);
      setListData(res.data?.leaves || []);
      setListPagination(res.data?.pagination || { current_page: 1, last_page: 1, total: 0 });
      applyFilterOptions(res);
    } catch (error) {
      console.error('خطا در دریافت لیست مرخصی‌ها:', error);
      message.error('خطا در دریافت لیست مرخصی‌ها');
    } finally {
      setListLoading(false);
    }
  };

  const loadCalendarData = useCallback(async (params = {}) => {
    if (!params.start_date || !params.end_date) {
      const now = moment();
      const y = now.jYear();
      const m = now.jMonth();
      const daysInMonth = moment.jDaysInMonth(y, m);
      params.start_date = `${y}/${String(m + 1).padStart(2, '0')}/01`;
      params.end_date = `${y}/${String(m + 1).padStart(2, '0')}/${String(daysInMonth).padStart(2, '0')}`;
    }

    // جلوگیری از ارسال تاریخ میلادی - تاریخ جلالی باید با 13 یا 14 شروع شود
    if (params.start_date && !String(params.start_date).match(/^1[34]/)) {
      console.warn('Blocked non-Jalali calendar request:', params.start_date);
      return;
    }

    if (!params.leave_type) params.leave_type = 'all';
    if (!params.status) params.status = 'all';
    if (!params.user_id) params.user_id = 'all';

    const myRequestId = ++calendarRequestId.current;
    setCalendarLoading(true);
    try {
      const res = await leavesApi.getCalendar(params);
      // فقط آخرین درخواست را اعمال کن
      if (calendarRequestId.current === myRequestId) {
        setCalendarLeaves(res?.leaves || res?.data?.leaves || []);
        applyFilterOptions(res);
      }
    } catch (error) {
      console.error('خطا در دریافت تقویم مرخصی‌ها:', error);
      if (calendarRequestId.current === myRequestId) {
        message.error('خطا در دریافت تقویم مرخصی‌ها');
      }
    } finally {
      if (calendarRequestId.current === myRequestId) {
        setCalendarLoading(false);
      }
    }
  }, [applyFilterOptions]);

  const loadPersonalData = async () => {
    setPersonalLoading(true);
    try {
      const res = await leavesApi.getPersonal();
      setPersonalData(res.data || null);
      applyFilterOptions(res);
    } catch (error) {
      console.error('خطا در دریافت اطلاعات شخصی:', error);
      message.error('خطا در دریافت اطلاعات شخصی');
    } finally {
      setPersonalLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'list') {
      loadListData(listFilters);
    } else if (viewMode === 'calendar') {
      loadCalendarData();
    } else if (viewMode === 'personal') {
      loadPersonalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <UserOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">مدیریت مرخصی‌ها</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">نمایش و مدیریت درخواست‌های مرخصی</p>
              </div>
            </div>
            <Space size="middle" wrap>
              <PermissionGate permission="add_leave">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/hr/leaves/add')}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  مرخصی جدید
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <div className="mb-4">
              <Segmented
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { label: 'لیست', value: 'list', icon: <UnorderedListOutlined /> },
                  { label: 'تقویم', value: 'calendar', icon: <CalendarOutlined /> },
                  { label: 'شخصی', value: 'personal', icon: <UserOutlined /> },
                ]}
                size="large"
                style={{ fontFamily: 'CustomFont' }}
              />
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              {viewMode === 'list' && (
                <LeavesListView
                  data={listData}
                  loading={listLoading}
                  pagination={listPagination}
                  filterOptions={filterOptions}
                  filterSnapshot={listFilters}
                  navigateToDetail={navigateToDetail}
                  onFilterChange={(f) => loadListData(f)}
                  onRefresh={(f) => loadListData(f)}
                  onDelete={canDelete ? async (id) => {
                    try {
                      await leavesApi.delete(id);
                      message.success('مرخصی با موفقیت حذف شد');
                      loadListData(listFilters);
                    } catch (error) {
                      message.error('خطا در حذف مرخصی');
                    }
                  } : null}
                  onEdit={canEdit ? (record) => navigateToDetail(`/hr/leaves/${record.id}/edit`) : null}
                />
              )}
              {viewMode === 'calendar' && (
                <LeavesCalendarView
                  leaves={calendarLeaves}
                  loading={calendarLoading}
                  filterOptions={filterOptions}
                  onFilterChange={loadCalendarData}
                />
              )}
              {viewMode === 'personal' && (
                <LeavesPersonalView
                  data={personalData}
                  loading={personalLoading}
                />
              )}
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Leaves;
